// =============================================================================
// OpportunityDetail — Universal 15-section CMS-driven page for ANY
// Business Opportunity (Product, Service, Franchise, Investment, etc.)
// Route: /opportunity/:slug
// Data:  GET /api/public/business-opportunities/:slug
// =============================================================================
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import * as Lucide from "lucide-react";
import { toast } from "sonner";
import WhatsAppCTA, { openWhatsApp } from "@/components/WhatsAppCTA";
import { formatApiErrorDetail } from "@/lib/api";
import { LanguageSwitcher, useT } from "@/i18n/LanguageContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const RED = "#C8102E";
const GOLD = "#D4AF37";
const CHARCOAL = "#1A1A1A";

// ---------- Section header primitive ----------
function SectionTag({ n, title }) {
  return (
    <div className="flex items-center gap-3 mb-6">
      <span
        className="grid place-items-center w-9 h-9 text-xs font-black text-white"
        style={{ background: CHARCOAL }}
      >
        {n}
      </span>
      <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tight">{title}</h2>
    </div>
  );
}

// ---------- Income Calculator (Sprint 2A wiring: computes + displays;
//                                 full editable CSS UI comes with 2B) ----------
function IncomeCalculator({ config, brand = "MIRESCO" }) {
  const { t } = useT();
  const currency = config?.currency || "RM";
  const [retail, setRetail] = useState(config?.retail_price || 0);
  const [cost, setCost] = useState(config?.your_cost || 0);
  const [units, setUnits] = useState(config?.default_units_per_day || 1);
  const days = config?.days_per_month || 30;
  const profitPerUnit = config?.profit_per_unit
    ? Number(config.profit_per_unit)
    : Math.max(0, Number(retail) - Number(cost));
  const daily = profitPerUnit * Number(units);
  const monthly = daily * Number(days);
  return (
    <div className="bg-white border-2 border-zinc-900 p-5 sm:p-6" data-testid="income-calculator">
      <div className="flex items-center gap-2 mb-4">
        <Lucide.Calculator className="w-5 h-5" style={{ color: RED }} />
        <div className="font-display font-black text-lg tracking-tight">{t("calc.title")}</div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <label className="text-xs">
          <div className="text-zinc-600 mb-1 uppercase tracking-widest font-semibold">{t("calc.retail")} ({currency})</div>
          <input
            type="number"
            value={retail}
            onChange={(e) => setRetail(e.target.value)}
            className="w-full border border-zinc-300 px-3 py-2 text-sm"
            data-testid="calc-retail"
          />
        </label>
        <label className="text-xs">
          <div className="text-zinc-600 mb-1 uppercase tracking-widest font-semibold">{t("calc.cost")} ({currency})</div>
          <input
            type="number"
            value={cost}
            onChange={(e) => setCost(e.target.value)}
            className="w-full border border-zinc-300 px-3 py-2 text-sm"
            data-testid="calc-cost"
          />
        </label>
        <label className="text-xs">
          <div className="text-zinc-600 mb-1 uppercase tracking-widest font-semibold">{t("calc.units")}</div>
          <input
            type="number"
            value={units}
            onChange={(e) => setUnits(e.target.value)}
            className="w-full border border-zinc-300 px-3 py-2 text-sm"
            data-testid="calc-units"
          />
        </label>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="border border-zinc-200 p-3">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">{t("calc.profit_unit")}</div>
          <div className="font-display font-black text-xl mt-1">{currency} {profitPerUnit.toFixed(2)}</div>
        </div>
        <div className="border border-zinc-200 p-3">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">{t("calc.daily")}</div>
          <div className="font-display font-black text-xl mt-1" style={{ color: RED }}>{currency} {daily.toFixed(2)}</div>
        </div>
        <div className="border-2 p-3" style={{ borderColor: GOLD }}>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">{t("calc.monthly")}</div>
          <div className="font-display font-black text-2xl mt-1" style={{ color: GOLD }} data-testid="calc-monthly">{currency} {monthly.toFixed(2)}</div>
        </div>
      </div>
      {config?.notes && (
        <div className="mt-4 text-xs text-zinc-500 italic">{config.notes}</div>
      )}
    </div>
  );
}

// ---------- CTA row: renders admin-defined action buttons ----------
function CtaRow({ ctas, waNumber, opp, onLead, rowId = "row" }) {
  const kindStyle = {
    buy:            { bg: RED,      label: "Buy" },
    affiliate:      { bg: GOLD,     label: "Become Affiliate" },
    agent:          { bg: CHARCOAL, label: "Become Agent" },
    stockist:       { bg: "#0e7c66",label: "Become Stockist" },
    distributor:    { bg: "#0e7c66",label: "Distributor" },
    kiosk:          { bg: "#5b3fbf",label: "Kiosk" },
    franchise:      { bg: "#5b3fbf",label: "Franchise" },
    territory_partner: { bg: CHARCOAL, label: "Territory Partner" },
    investor:       { bg: CHARCOAL, label: "Investor" },
    apply:          { bg: RED,      label: "Apply" },
    whatsapp:       { bg: "#25D366",label: "WhatsApp Us" },
    external:       { bg: CHARCOAL, label: "Learn More" },
    learn:          { bg: CHARCOAL, label: "Learn More" },
  };

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2" data-testid={`opp-cta-row-${rowId}`}>
      {(ctas || []).filter((c) => c.visible !== false).map((c, i) => {
        const style = kindStyle[c.kind] || { bg: CHARCOAL, label: c.label };
        const label = c.label || style.label;
        const onClick = () => {
          onLead?.(c.kind);
          if (c.kind === "external" && c.url) {
            window.open(c.url, "_blank", "noopener");
            return;
          }
          if (c.kind === "buy" && c.url) {
            window.open(c.url, "_blank", "noopener");
            return;
          }
          const tpl = c.wa_template || `Hi ${opp.brand || "MIRESCO"}! ${label} — ${opp.title}`;
          openWhatsApp(c.wa_template ? waNumber : waNumber, tpl);
        };
        return (
          <button
            key={`${c.kind}-${i}`}
            onClick={onClick}
            data-testid={`opp-cta-${rowId}-${c.kind}`}
            className="text-white text-sm font-semibold px-3 py-3 hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
            style={{ background: style.bg }}
          >
            {label}
            <Lucide.ArrowRight className="w-3.5 h-3.5" />
          </button>
        );
      })}
    </div>
  );
}

// ---------- Photos grid ----------
function MediaGrid({ items, kind = "photo" }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
      {items.map((m, i) => (
        <div key={i} className="border border-zinc-200 overflow-hidden bg-zinc-50">
          {kind === "video" ? (
            <video src={m.url} controls preload="metadata" className="w-full aspect-video object-cover" />
          ) : (
            <img src={m.url} alt={m.title || "media"} className="w-full aspect-square object-cover" />
          )}
          {(m.title || m.caption) && (
            <div className="p-2 text-xs">
              {m.title && <div className="font-semibold">{m.title}</div>}
              {m.caption && <div className="text-zinc-500 mt-0.5">{m.caption}</div>}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ---------- Testimonials ----------
function Testimonials({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {items.map((t, i) => (
        <div key={i} className="border border-zinc-200 p-5 bg-white">
          {t.video_url ? (
            <video src={t.video_url} controls preload="metadata" className="w-full aspect-video mb-3 bg-zinc-100" />
          ) : t.photo_url ? (
            <img src={t.photo_url} alt={t.name || "testimonial"} className="w-16 h-16 rounded-full object-cover mb-3" />
          ) : null}
          {t.quote && <div className="text-sm text-zinc-700 italic">&ldquo;{t.quote}&rdquo;</div>}
          <div className="mt-3 text-xs text-zinc-500">
            <span className="font-semibold text-zinc-900">{t.name}</span>
            {t.role ? ` · ${t.role}` : ""}
            {t.location ? ` · ${t.location}` : ""}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------- FAQ (simple accordion) ----------
function FAQ({ items }) {
  const [open, setOpen] = useState(-1);
  if (!items || items.length === 0) return null;
  return (
    <div className="divide-y border-y border-zinc-200">
      {items.map((f, i) => (
        <div key={i}>
          <button
            onClick={() => setOpen(open === i ? -1 : i)}
            className="w-full text-left flex items-start justify-between gap-3 py-4"
            data-testid={`faq-toggle-${i}`}
          >
            <span className="font-semibold text-sm sm:text-base">{f.q}</span>
            <Lucide.ChevronDown className={`w-5 h-5 shrink-0 transition-transform ${open === i ? "rotate-180" : ""}`} />
          </button>
          {open === i && <div className="pb-4 text-sm text-zinc-700 leading-relaxed">{f.a}</div>}
        </div>
      ))}
    </div>
  );
}

// ---------- Section wrapper ----------
function Section({ id, n, title, children, tone = "white" }) {
  const bg = tone === "grey" ? "#FAFAFA" : "#FFFFFF";
  return (
    <section id={id} className="border-b border-zinc-200" style={{ background: bg }}>
      <div className="max-w-5xl mx-auto px-5 lg:px-10 py-12 lg:py-16">
        <SectionTag n={n} title={title} />
        {children}
      </div>
    </section>
  );
}

// ---------- Enquiry Form (structured lead capture) ----------
function EnquiryForm({ slug, brand, title }) {
  const { t } = useT();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [interest, setInterest] = useState("affiliate");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) {
      toast.error(t("enq.err_required"));
      return;
    }
    setBusy(true);
    try {
      // Attach referral code if we captured one from ?ref=CODE (stored in localStorage)
      let refCode = "";
      try { refCode = localStorage.getItem("miresco_ref") || ""; } catch { /* private mode */ }
      await axios.post(`${BACKEND}/api/opportunity-leads`, {
        opportunity_slug: slug,
        cta_kind: interest,
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim(),
        message: message.trim() || `I'm interested in ${title} as a ${interest}. Please contact me.`,
        referral_code: refCode,
      });
      setDone(true);
      toast.success(t("enq.done_toast"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="bg-white text-zinc-900 border-2 p-6 sm:p-8 text-center max-w-xl mx-auto" style={{ borderColor: RED }} data-testid="enquiry-form-done">
        <div className="text-4xl mb-2">✅</div>
        <div className="font-display font-black text-xl tracking-tight">{t("enq.done_title")}</div>
        <div className="text-sm text-zinc-600 mt-2">{t("enq.done_sub")}</div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-white text-zinc-900 border-2 border-zinc-900 p-5 sm:p-7 max-w-xl mx-auto text-left" data-testid="enquiry-form">
      <div className="flex items-center gap-2 mb-4">
        <Lucide.Mail className="w-5 h-5" style={{ color: RED }} />
        <div className="font-display font-black text-lg tracking-tight">{t("enq.title")}</div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t("enq.name")}
          className="border border-zinc-300 px-3 py-2.5 text-sm"
          data-testid="enq-name"
        />
        <input
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder={t("enq.phone")}
          className="border border-zinc-300 px-3 py-2.5 text-sm"
          data-testid="enq-phone"
        />
      </div>
      <input
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder={t("enq.email")}
        className="w-full border border-zinc-300 px-3 py-2.5 text-sm mb-3"
        data-testid="enq-email"
      />
      <select
        value={interest}
        onChange={(e) => setInterest(e.target.value)}
        className="w-full border border-zinc-300 px-3 py-2.5 text-sm bg-white mb-3"
        data-testid="enq-interest"
      >
        <option value="buy">{t("enq.opt_buy")}</option>
        <option value="affiliate">{t("enq.opt_affiliate")}</option>
        <option value="agent">{t("enq.opt_agent")}</option>
        <option value="stockist">{t("enq.opt_stockist")}</option>
        <option value="apply">{t("enq.opt_general")}</option>
      </select>
      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        rows={2}
        placeholder={t("enq.message")}
        className="w-full border border-zinc-300 px-3 py-2.5 text-sm mb-4"
        data-testid="enq-message"
      />
      <button
        type="submit"
        disabled={busy}
        className="w-full inline-flex items-center justify-center gap-2 bg-[#C8102E] hover:bg-[#9A0C24] disabled:opacity-50 text-white px-5 py-3 font-semibold text-sm"
        data-testid="enq-submit"
      >
        {busy ? t("enq.submitting") : `${t("enq.submit")} ${brand || "MIRESCO"}`}
        <Lucide.ArrowRight className="w-4 h-4" />
      </button>
      <div className="mt-3 text-xs text-zinc-500 text-center">{t("enq.footer")}</div>
    </form>
  );
}

// ---------- CMS-driven Engagement Tab (Start Earning / Creator Campaign) ----------
function EngagementTabPanel({ tab, opp, waNumber, onLead, testIdKey }) {
  const { t } = useT();
  if (!tab || tab.enabled === false) {
    return (
      <div className="max-w-3xl mx-auto px-5 lg:px-10 py-14 text-center text-zinc-500 text-sm" data-testid={`opp-tab-${testIdKey}-empty`}>
        {t("opp.tab_empty")}
      </div>
    );
  }
  const brand = opp?.brand || "MIRESCO";
  const heading = tab.heading || (testIdKey === "start-earning" ? `${t("opp.tab_default_earn_heading")} ${brand}` : `${brand} ${t("opp.tab_default_creator_heading")}`);
  const ctaKindStyle = {
    buy: RED, affiliate: GOLD, agent: CHARCOAL, stockist: "#0e7c66",
    distributor: "#0e7c66", kiosk: "#5b3fbf", franchise: "#5b3fbf",
    territory_partner: CHARCOAL, investor: CHARCOAL, apply: RED,
    whatsapp: "#25D366", external: CHARCOAL, learn: CHARCOAL,
  };
  const bg = ctaKindStyle[tab.cta_kind] || RED;

  const clickCta = () => {
    onLead?.(tab.cta_kind || "apply");
    if ((tab.cta_kind === "external" || tab.cta_kind === "buy") && tab.cta_url) {
      window.open(tab.cta_url, "_blank", "noopener");
      return;
    }
    const tpl = tab.wa_template || `Hi ${brand}! ${heading} — ${opp.title}`;
    openWhatsApp(waNumber, tpl);
  };

  return (
    <div className="max-w-5xl mx-auto px-5 lg:px-10 py-12 lg:py-16" data-testid={`opp-tab-${testIdKey}-panel`}>
      {tab.image_url && (
        <div className="mb-8 border border-zinc-200 overflow-hidden">
          <img src={tab.image_url} alt={heading} className="w-full aspect-[21/9] object-cover" />
        </div>
      )}
      <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight leading-tight" data-testid={`opp-tab-${testIdKey}-heading`}>{heading}</h2>
      {tab.subheading && <p className="mt-3 text-lg text-zinc-700 max-w-3xl">{tab.subheading}</p>}
      {tab.body && <p className="mt-6 text-base text-zinc-800 leading-relaxed whitespace-pre-line max-w-3xl">{tab.body}</p>}

      {tab.video_url && (
        <div className="mt-8 border border-zinc-200 max-w-3xl">
          <video src={tab.video_url} controls preload="metadata" className="w-full aspect-video" />
        </div>
      )}

      {(tab.bullets || []).length > 0 && (
        <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-4xl">
          {(tab.bullets || []).filter(Boolean).map((b, i) => (
            <li key={i} className="flex items-start gap-3 border border-zinc-200 p-4 bg-white" data-testid={`opp-tab-${testIdKey}-bullet-${i}`}>
              <div className="grid place-items-center w-7 h-7 shrink-0 text-white text-xs font-black" style={{ background: RED }}>
                {i + 1}
              </div>
              <div className="text-sm text-zinc-800 leading-relaxed">{b}</div>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <button
          onClick={clickCta}
          className="inline-flex items-center gap-2 text-white text-sm font-semibold px-5 py-3 hover:opacity-90 transition-opacity"
          style={{ background: bg }}
          data-testid={`opp-tab-${testIdKey}-cta`}
        >
          {tab.cta_label || (testIdKey === "start-earning" ? t("opp.tab_cta_earn_default") : t("opp.tab_cta_creator_default"))}
          <Lucide.ArrowRight className="w-4 h-4" />
        </button>
        {tab.journey_slug && (
          <Link
            to={`/journey/${tab.journey_slug}`}
            className="inline-flex items-center gap-2 text-sm font-semibold px-5 py-3 border-2 border-zinc-900 hover:bg-zinc-900 hover:text-white transition-colors"
            data-testid={`opp-tab-${testIdKey}-journey`}
          >
            <Lucide.MapPin className="w-4 h-4" /> {t("opp.tab_follow_journey")}
          </Link>
        )}
      </div>
    </div>
  );
}

// ---------- Main page ----------
export default function OpportunityDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t } = useT();
  const [opp, setOpp] = useState(null);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("business"); // business | start-earning | creator-campaign
  const waNumber = opp?.whatsapp_number || "";

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${BACKEND}/api/public/business-opportunities/${slug}`)
      .then((r) => mounted && setOpp(r.data))
      .catch((err) => {
        if (!mounted) return;
        const msg = formatApiErrorDetail(err?.response?.data?.detail) || "Opportunity not found";
        setError(msg);
      });
    return () => {
      mounted = false;
    };
  }, [slug]);

  const trackLead = (ctaKind) => {
    axios
      .post(`${BACKEND}/api/opportunity-leads`, {
        opportunity_slug: slug,
        cta_kind: ctaKind || "apply",
        name: "",
        phone: "",
        email: "",
        message: `Clicked ${ctaKind} on ${opp?.title || slug}`,
      })
      .catch(() => {});
  };

  const heroBg = useMemo(() => {
    if (opp?.hero_image_url) return `url(${opp.hero_image_url})`;
    return `linear-gradient(135deg, ${RED} 0%, #7c0a1e 100%)`;
  }, [opp]);

  if (error) {
    return (
      <div className="min-h-screen bg-white grid place-items-center px-5">
        <div className="text-center">
          <div className="text-5xl mb-3">🔍</div>
          <h1 className="font-display font-black text-2xl tracking-tight">{t("opp.not_found")}</h1>
          <p className="text-zinc-600 mt-2 text-sm">{error}</p>
          <Link to="/" className="inline-flex items-center gap-2 mt-6 text-sm font-semibold" style={{ color: RED }}>
            <Lucide.ArrowLeft className="w-4 h-4" /> {t("opp.back_home")}
          </Link>
        </div>
      </div>
    );
  }
  if (!opp) {
    return (
      <div className="min-h-screen bg-white grid place-items-center">
        <div className="font-mono text-sm text-zinc-500">{t("opp.loading")}</div>
      </div>
    );
  }

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen bg-white text-[#09090b]">
      {/* Sticky top bar */}
      <header className="border-b border-zinc-200 bg-white sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-5 lg:px-10 h-14 flex items-center justify-between gap-3">
          <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm font-semibold hover:text-red-700" data-testid="opp-back">
            <Lucide.ArrowLeft className="w-4 h-4" /> {t("opp.back")}
          </button>
          <div className="text-xs uppercase tracking-widest font-semibold text-zinc-500 truncate max-w-[60%]">
            {opp.brand || "MIRESCO"} · {opp.category_slug}
          </div>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <WhatsAppCTA template={`Hi ${opp.brand || "MIRESCO"}! I want to learn more about ${opp.title}.`} label={t("opp.header_chat")} variant="compact" overrideNumber={waNumber} testId="opp-header-wa" />
          </div>
        </div>
      </header>

      {/* Hero */}
      <section
        className="relative overflow-hidden text-white"
        style={{ background: heroBg, backgroundSize: "cover", backgroundPosition: "center" }}
      >
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.7) 100%)" }} />
        <div className="relative max-w-6xl mx-auto px-5 lg:px-10 py-16 lg:py-24">
          <div className="inline-flex items-center gap-2 text-[11px] font-semibold px-3 py-1.5 rounded-full mb-4" style={{ background: `${GOLD}dd`, color: CHARCOAL }} data-testid="opp-nature">
            {opp.opportunity_nature || "Business Opportunity"}
          </div>
          <h1 className="font-display font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05]" data-testid="opp-title">
            {opp.title}
          </h1>
          {opp.tagline && <p className="mt-4 text-lg sm:text-xl opacity-90 max-w-3xl">{opp.tagline}</p>}
          {opp.revenue_model && (
            <div className="mt-6 max-w-3xl">
              <div className="text-[10px] uppercase tracking-widest font-semibold opacity-70">{t("opp.sec_revenue")}</div>
              <div className="text-sm sm:text-base opacity-95">{opp.revenue_model}</div>
            </div>
          )}
          <div className="mt-8">
            <CtaRow ctas={opp.ctas} waNumber={waNumber} opp={opp} onLead={trackLead} rowId="hero" />
          </div>
          <div className="mt-6 flex flex-wrap gap-2 text-xs">
            <button onClick={() => scrollTo("sec-income")} className="px-3 py-1.5 bg-white/10 hover:bg-white/20 border border-white/20">{t("opp.hero_estimate")}</button>
            <button onClick={() => scrollTo("sec-apply")} className="px-3 py-1.5 bg-white/10 hover:bg-white/20 border border-white/20">{t("opp.hero_join")}</button>
            <button onClick={() => scrollTo("sec-faq")} className="px-3 py-1.5 bg-white/10 hover:bg-white/20 border border-white/20">{t("opp.hero_faq")}</button>
          </div>
        </div>
      </section>

      {/* Guided Growth engagement tabs — 3 CMS-driven tabs */}
      <div className="sticky top-14 z-20 bg-white border-b border-zinc-200" data-testid="opp-tabs-bar">
        <div className="max-w-6xl mx-auto px-5 lg:px-10">
          <div className="flex overflow-x-auto no-scrollbar" role="tablist">
            {[
              { id: "business",         label: t("opp.tab_business"), icon: Lucide.Briefcase },
              { id: "start-earning",    label: t("opp.tab_earn"),     icon: Lucide.Rocket },
              { id: "creator-campaign", label: t("opp.tab_creator"),  icon: Lucide.Video },
            ].map((tab) => {
              const active = activeTab === tab.id;
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  role="tab"
                  aria-selected={active}
                  onClick={() => setActiveTab(tab.id)}
                  data-testid={`opp-tab-btn-${tab.id}`}
                  className={`inline-flex items-center gap-2 px-4 sm:px-6 py-3.5 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors ${active ? "text-zinc-900" : "text-zinc-500 hover:text-zinc-900"}`}
                  style={{ borderColor: active ? RED : "transparent" }}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {activeTab === "business" && (<>
      {/* 1. Summary */}
      <Section id="sec-summary" n={1} title={t("opp.sec_summary")} tone="white">
        <p className="text-lg text-zinc-800 leading-relaxed max-w-3xl" data-testid="opp-summary">{opp.summary || "—"}</p>
      </Section>

      {/* 2. Overview */}
      <Section id="sec-overview" n={2} title={t("opp.sec_overview")} tone="grey">
        <p className="text-base text-zinc-700 leading-relaxed whitespace-pre-line max-w-3xl">{opp.overview || "—"}</p>
      </Section>

      {/* 3. Opportunity Nature */}
      <Section id="sec-nature" n={3} title={t("opp.sec_nature")} tone="white">
        <div className="inline-block px-4 py-2 border-2 font-semibold" style={{ borderColor: RED, color: RED }}>{opp.opportunity_nature || "—"}</div>
      </Section>

      {/* 4. Revenue Model */}
      <Section id="sec-revenue" n={4} title={t("opp.sec_revenue")} tone="grey">
        <div className="max-w-3xl border-l-4 pl-4 py-2" style={{ borderColor: GOLD }}>
          <div className="text-base text-zinc-800">{opp.revenue_model || "—"}</div>
        </div>
      </Section>

      {/* 5 + 6. Investment + Estimated Income (side-by-side) */}
      <section className="border-b border-zinc-200 bg-white">
        <div className="max-w-5xl mx-auto px-5 lg:px-10 py-12 lg:py-16 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <SectionTag n={5} title={t("opp.sec_invest")} />
            <div className="text-base text-zinc-800 whitespace-pre-line" data-testid="opp-investment">{opp.investment_required || "—"}</div>
          </div>
          <div>
            <SectionTag n={6} title={t("opp.sec_income")} />
            <div className="text-base font-semibold whitespace-pre-line" style={{ color: RED }} data-testid="opp-income">{opp.estimated_income || "—"}</div>
          </div>
        </div>
      </section>

      {/* 7. Business Model */}
      <Section id="sec-model" n={7} title={t("opp.sec_model")} tone="grey">
        <p className="text-base text-zinc-700 leading-relaxed whitespace-pre-line max-w-3xl">{opp.business_model || "—"}</p>
      </Section>

      {/* 8. Requirements */}
      <Section id="sec-requirements" n={8} title={t("opp.sec_reqs")} tone="white">
        <ul className="max-w-2xl space-y-2">
          {(opp.requirements || []).map((r, i) => (
            <li key={i} className="flex items-start gap-2 text-base">
              <Lucide.Check className="w-5 h-5 shrink-0 mt-0.5" style={{ color: RED }} /> {r}
            </li>
          ))}
          {(opp.requirements || []).length === 0 && <li className="text-zinc-500">—</li>}
        </ul>
      </Section>

      {/* 9. Training */}
      <Section id="sec-training" n={9} title={t("opp.sec_training")} tone="grey">
        <p className="text-base text-zinc-700 leading-relaxed whitespace-pre-line max-w-3xl">{opp.training || "—"}</p>
      </Section>

      {/* Income Calculator (moves between #6 and next) */}
      {opp.calculator?.enabled && (
        <section id="sec-income" className="border-b border-zinc-200 bg-white">
          <div className="max-w-5xl mx-auto px-5 lg:px-10 py-12 lg:py-16">
            <IncomeCalculator config={opp.calculator} brand={opp.brand} />
          </div>
        </section>
      )}

      {/* 10. Videos */}
      {(opp.videos || []).length > 0 && (
        <Section id="sec-videos" n={10} title={t("opp.sec_videos")} tone="white">
          <MediaGrid items={opp.videos} kind="video" />
        </Section>
      )}

      {/* 11. Photos */}
      {(opp.photos || []).length > 0 && (
        <Section id="sec-photos" n={11} title={t("opp.sec_photos")} tone="grey">
          <MediaGrid items={opp.photos} kind="photo" />
        </Section>
      )}

      {/* 12. Testimonials */}
      {(opp.testimonials || []).length > 0 && (
        <Section id="sec-testimonials" n={12} title={t("opp.sec_testimonials")} tone="white">
          <Testimonials items={opp.testimonials} />
        </Section>
      )}

      {/* 13. Success Stories */}
      {(opp.success_stories || []).length > 0 && (
        <Section id="sec-success" n={13} title={t("opp.sec_success")} tone="grey">
          <MediaGrid items={opp.success_stories} kind="photo" />
        </Section>
      )}

      {/* 14. FAQ */}
      <Section id="sec-faq" n={14} title={t("opp.sec_faq")} tone="white">
        <FAQ items={opp.faq} />
      </Section>

      {/* 15. Apply row */}
      <section id="sec-apply" className="border-b border-zinc-200" style={{ background: CHARCOAL }}>
        <div className="max-w-5xl mx-auto px-5 lg:px-10 py-14 lg:py-20 text-white text-center">
          <div className="inline-flex items-center gap-2 text-[11px] font-semibold px-3 py-1.5 rounded-full mb-4" style={{ background: `${GOLD}30`, color: GOLD }}>
            <Lucide.Sparkles className="w-3.5 h-3.5" /> {t("opp.apply_ready")}
          </div>
          <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight">{t("opp.apply_heading")} {opp.brand || "MIRESCO"}</h2>
          <p className="text-white/70 mt-3 max-w-2xl mx-auto">{t("opp.apply_sub")}</p>
          <div className="mt-8">
            <CtaRow ctas={opp.ctas} waNumber={waNumber} opp={opp} onLead={trackLead} rowId="apply" />
          </div>
          <div className="mt-10">
            <EnquiryForm slug={slug} brand={opp.brand} title={opp.title} />
          </div>
        </div>
      </section>
      </>)}

      {activeTab === "start-earning" && (
        <section className="border-b border-zinc-200 bg-white" data-testid="opp-tab-start-earning">
          <EngagementTabPanel
            tab={opp.tab_start_earning}
            opp={opp}
            waNumber={waNumber}
            onLead={trackLead}
            testIdKey="start-earning"
          />
        </section>
      )}

      {activeTab === "creator-campaign" && (
        <section className="border-b border-zinc-200 bg-white" data-testid="opp-tab-creator-campaign">
          <EngagementTabPanel
            tab={opp.tab_creator_campaign}
            opp={opp}
            waNumber={waNumber}
            onLead={trackLead}
            testIdKey="creator-campaign"
          />
        </section>
      )}
    </div>
  );
}
